import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { institutionalColors, primaryColors } from '~/design'

interface TabsContextType {
  activeTab: string
  setActiveTab: (id: string) => void
  isAnimated?: boolean
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tab components must be used within Tabs')
  }
  return context
}

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const TabListContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${institutionalColors.graySebrae90};
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: thin;
  scrollbar-color: ${institutionalColors.graySebrae60} transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${institutionalColors.graySebrae60};
    border-radius: 2px;
  }
`

const TabButton = styled.button<{ $isActive: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px 12px;
  background: ${({ $isActive }) => ($isActive ? '#E0E5EB4D' : '#fff')};
  border: none;
  border-bottom: 4px solid transparent;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 1rem;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '500')};
  color: ${({ $isActive, $disabled }) =>
    $disabled
      ? institutionalColors.graySebrae75
      : $isActive
        ? institutionalColors.blueSebrae36
        : institutionalColors.blueSebrae90};
  transition: all 0.2s ease;
  white-space: nowrap;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  position: relative;

  ${({ $isActive, $disabled }) =>
    $isActive &&
    !$disabled &&
    `
    border-bottom-color: ${institutionalColors.blueSebrae36};
  `}

  &:hover:not(:disabled) {
    color: ${({ $isActive }) => ($isActive ? primaryColors.action : primaryColors.active)};
    background: #fff;
  }

  &:focus-visible {
    outline: 2px solid ${primaryColors.active};
    outline-offset: 2px;
  }

  svg {
    font-size: 1.2rem;
  }
`

const TabButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TabButtonSubtitle = styled.span`
  font-size: 0.7rem;
  font-weight: 400;
`

const TabPanelContainer = styled.div<{ $isActive: boolean; $isAnimated?: boolean }>`
  display: block;
  padding: 12px 0 0;
  width: 100%;

  ${({ $isActive, $isAnimated }) =>
    $isActive
      ? `
    visibility: visible;
    opacity: 1;
    height: auto;
    animation: ${$isAnimated ? 'fadeIn 0.3s ease-in' : 'none'};
  `
      : `
    visibility: hidden;
    opacity: 0;
    height: 0;
    padding: 0;
    overflow: hidden;
    pointer-events: none;
  `}

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export interface TabItem {
  id: string
  title: string
  icon?: ReactNode
  disabled?: boolean
}

interface TabsProps {
  children: ReactNode
  defaultTab?: string
  value?: string
  onChange?: (tabId: string) => void
  className?: string
  isAnimated?: boolean
}

interface TabListProps {
  children: ReactNode
}

interface TabProps {
  id: string
  title?: string
  subtitle?: string
  icon?: ReactNode
  disabled?: boolean
}

interface TabPanelProps {
  id: string
  children: ReactNode
}

export function Tabs({
  children,
  defaultTab,
  value,
  onChange,
  className,
  isAnimated = true,
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || '')

  const activeTab = value !== undefined ? value : internalActiveTab
  const setActiveTab = (id: string) => {
    if (value === undefined) {
      setInternalActiveTab(id)
    }
    onChange?.(id)
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab, isAnimated }}>
      <TabsContainer className={className}>{children}</TabsContainer>
    </TabsContext.Provider>
  )
}

export function TabList({ children }: TabListProps) {
  return <TabListContainer>{children}</TabListContainer>
}

export function Tab({ id, title, subtitle, icon, disabled }: TabProps) {
  const { activeTab, setActiveTab } = useTabsContext()
  const isActive = activeTab === id

  return (
    <TabButton
      role='tab'
      aria-selected={isActive}
      aria-controls={`panel-${id}`}
      id={`tab-${id}`}
      $isActive={isActive}
      $disabled={disabled}
      disabled={disabled}
      onClick={() => !disabled && setActiveTab(id)}
    >
      <TabButtonContent>
        {title && <span>{title}</span>}
        {subtitle && <TabButtonSubtitle>{subtitle}</TabButtonSubtitle>}
      </TabButtonContent>
      {icon && <span>{icon}</span>}
    </TabButton>
  )
}

export function TabPanel({ id, children }: TabPanelProps) {
  const { activeTab, isAnimated } = useTabsContext()
  const isActive = activeTab === id

  return (
    <TabPanelContainer
      role='tabpanel'
      aria-labelledby={`tab-${id}`}
      id={`panel-${id}`}
      $isActive={isActive}
      $isAnimated={isAnimated}
    >
      {children}
    </TabPanelContainer>
  )
}
